import React, { useMemo } from "react";
import { formatDate } from "../../../../components/common/Converter";

// Constants
const LEAVE_TYPE_MAPPING = {
  "SOLO PARENT LEAVE": "SOLO",
  "STUDY LEAVE": "STUDY",
  "SPECIAL LEAVE BENEFITS FOR WOMEN": "MC",
  "SPECIAL EMERGENCY LEAVE": "CALAMITY",
};

const LEAVE_TYPE_INDEXES = {
  "Vacation Leave": [0],
  "Mandayory/Forced Leave": [1],
  "Sick Leave": [2],
  "Maternity Leave": [3],
  "Paternity Leave": [4],
  "Special Leave Privilege": [5],
  "Solo Parent Leave": [6],
  "Study Leave": [7],
  "10-Day VAWC Leave": [8],
  "Rehabilitation Privilege": [9],
  "Special Leave Benefits for Women": [10],
  "Special Emergency (Calamity) Leave": [11],
  "Adoption Leave": [12],
};

const PRINT_STYLES = {
  container: {
    width: "8.27in",
    height: "11.69in",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    marginLeft: "-10px",
    marginTop: "-10px",
    fontSize: "11px",
  },
  image: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
  basePosition: {
    width: "10rem",
    position: "absolute",
    textAlign: "center",
    top: "14.1rem",
    left: "5.4rem",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
};

// Utility functions
function getLeaveTypeAbbreviation(leaveType) {
  if (!leaveType) return "";

  const upperLeaveType = leaveType.toUpperCase();

  if (LEAVE_TYPE_MAPPING[upperLeaveType]) {
    return LEAVE_TYPE_MAPPING[upperLeaveType];
  }

  return upperLeaveType
    .split(" ")
    .map((word) => word.charAt(0))
    .join("");
}

function calculateTotalDays(dateRange) {
  if (!dateRange?.length) return 0;

  let totalDays = 0;

  dateRange.forEach((range) => {
    range.forEach(([startDate, endDate]) => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = end - start;
      const diffDays = Math.ceil(diffTime / (1000 * 3600 * 24));
      totalDays += diffDays + 1;
    });
  });

  return totalDays;
}

function parseNumericValue(value, defaultValue = 0) {
  if (value === null || value === undefined || value === "")
    return defaultValue;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

export default function HRMSPrint({
  object,
  vacationLeave,
  sickLeave,
  department,
}) {
  // Memoized calculations
  const calculations = useMemo(() => {
    const duration = object?.duration?.split(" ");
    const durationDays = parseNumericValue(duration?.[0]);
    const totalDays = calculateTotalDays(object?.dateRange);

    let withoutPay = Math.max(0, durationDays - totalDays);
    const finalTotalDays =
      object?.categoryType === "Without Pay" ? 0 : totalDays;

    // Leave balances
    const vacationCurrent = parseNumericValue(vacationLeave?.[0]?.current);
    const sickCurrent = parseNumericValue(sickLeave?.[0]?.current);
    const creditDeduction = parseNumericValue(object?.creditDeduction);

    let sickDeduct = 0;
    let vacationDeduct = 0;
    let totalEarnedVacation = vacationCurrent;
    let totalEarnedSickLeave = sickCurrent;
    let typeOfLeaveWithPay = "";
    // 7.C: days with pay / others (SL/WL overflow to VL)
    let daysWithPayDisplay = "";
    let othersSpecify = "";

    if (object?.categoryType === "With Pay") {
      const leaveTypeName = object?.leaveType?.name;
      const isSickOrWellness =
        leaveTypeName === "Sick Leave" || leaveTypeName === "Wellness Leave";

      if (isSickOrWellness) {
        // creditDeduction = full request; vacationLeaveDeduction = overflow to VL
        const vacationDeduction = parseNumericValue(
          object?.vacationLeaveDeduction,
        );
        const leavePortion = Math.max(
          0,
          parseFloat((creditDeduction - vacationDeduction).toFixed(3)),
        );
        const abbrev = leaveTypeName === "Sick Leave" ? "SL" : "WL";

        totalEarnedSickLeave = sickCurrent + leavePortion;
        sickDeduct = leavePortion;
        totalEarnedVacation = vacationCurrent + vacationDeduction;
        vacationDeduct = vacationDeduction;

        // days with pay = portion from SL/WL; empty if all went to VL
        daysWithPayDisplay =
          leavePortion > 0 ? `${leavePortion} ${abbrev}` : "";
        // others = overflow charged against VL
        othersSpecify =
          vacationDeduction > 0
            ? `${vacationDeduction.toFixed(1)} ${abbrev} to VL`
            : "";
        typeOfLeaveWithPay = abbrev;
      } else if (leaveTypeName === "Vacation Leave") {
        totalEarnedVacation = vacationCurrent + creditDeduction;
        vacationDeduct = creditDeduction;
        typeOfLeaveWithPay = getLeaveTypeAbbreviation(leaveTypeName);
        daysWithPayDisplay =
          finalTotalDays > 0
            ? `${finalTotalDays} ${typeOfLeaveWithPay}`
            : "";
      } else {
        typeOfLeaveWithPay = getLeaveTypeAbbreviation(leaveTypeName);
        daysWithPayDisplay =
          finalTotalDays > 0
            ? `${finalTotalDays} ${typeOfLeaveWithPay}`
            : "";
        // Non-deductible leave types use duration for "others"
        if (!object?.leaveType?.is_deductable) {
          othersSpecify = object?.duration || "";
        }
      }
    }

    return {
      durationDays,
      totalDays: finalTotalDays,
      withoutPay,
      sickDeduct,
      vacationDeduct,
      totalEarnedVacation,
      totalEarnedSickLeave,
      currentBalanceVacation: vacationCurrent,
      currentBalanceSickLeave: sickCurrent,
      typeOfLeaveWithPay,
      daysWithPayDisplay,
      othersSpecify,
    };
  }, [object, vacationLeave, sickLeave]);

  // Helper component for positioning data
  const DataPosition = ({ children, style = {} }) => (
    <div style={{ ...PRINT_STYLES.basePosition, ...style }}>{children}</div>
  );

  // Helper function to display days with proper pluralization
  const displayDays = (days) => `${days} Day${days === 1 ? "" : "s"}`;

  // Render leave type checkmarks
  const renderLeaveTypeCheckmarks = () => {
    const leaveType = object?.leaveType?.name;
    const indexes = LEAVE_TYPE_INDEXES[leaveType];

    if (!indexes) return null;

    return indexes.map((index) => (
      <DataPosition
        key={index}
        style={{
          left: "5.9rem",
          width: "1rem",
          top: `${
            index <= 1 ? 19.9 + index * 1.2 : 19.9 + 1 * 1.2 + (index - 1) * 1.1
          }rem`,
          fontSize: "10px",
        }}
      >
        ✔
      </DataPosition>
    ));
  };

  // Render location/treatment checkmarks and details
  const renderLocationDetails = () => (
    <>
      {object?.philippines && (
        <>
          <DataPosition
            style={{ left: "27.2rem", width: "1rem", top: "21rem" }}
          >
            ✔
          </DataPosition>
          <DataPosition
            style={{ left: "34.2rem", width: "9rem", top: "21rem" }}
          >
            {object?.abroadspecify}
          </DataPosition>
        </>
      )}

      {object?.abroad && (
        <>
          <DataPosition
            style={{ left: "27.2rem", width: "1rem", top: "22.1rem" }}
          >
            ✔
          </DataPosition>
          <DataPosition
            style={{ left: "32.9rem", width: "10rem", top: "22.1rem" }}
          >
            {object?.abroadspecify}
          </DataPosition>
        </>
      )}

      {object?.hospital && (
        <>
          <DataPosition
            style={{ left: "27.2rem", width: "1rem", top: "24.4rem" }}
          >
            ✔
          </DataPosition>
          <DataPosition
            style={{ left: "35.7rem", width: "7.5rem", top: "24.4rem" }}
          >
            {object?.inhospital}
          </DataPosition>
        </>
      )}

      {object?.patient && (
        <>
          <DataPosition
            style={{ left: "27.2rem", width: "1rem", top: "25.4rem" }}
          >
            ✔
          </DataPosition>
          <DataPosition
            style={{ left: "36rem", width: "7.2rem", top: "25.5rem" }}
          >
            {object?.outpatient}
          </DataPosition>
        </>
      )}
    </>
  );

  return (
    <div style={PRINT_STYLES.container}>
      <img
        src="/leaveTemplate.png"
        style={PRINT_STYLES.image}
        alt="Leave Template"
      />

      {/* Employee Information */}
      <DataPosition
        style={{
          height: "23px",
          fontSize: "10px",
          lineHeight: "1",
          top: "13.5rem",
          whiteSpace: "normal",
          width: "11.5rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        }}
      >
        {department || ""}
      </DataPosition>

      <DataPosition style={{ left: "21.6rem", width: "6.5rem" }}>
        {object?.employee?.surname}
      </DataPosition>

      <DataPosition style={{ left: "28.5rem", width: "6.8rem" }}>
        {object?.employee?.Firstname}
      </DataPosition>

      <DataPosition style={{ left: "35.5rem", width: "6.5rem" }}>
        {object?.employee?.Middlename}
      </DataPosition>

      {/* Position and Salary Information */}
      <DataPosition
        style={{ left: "11.4rem", width: "5.2rem", top: "15.6rem" }}
      >
        {formatDate(object?.createdAt)}
      </DataPosition>

      <DataPosition
        style={{ left: "23.1rem", width: "10.2rem", top: "15.6rem" }}
      >
        {object?.employee?.position?.position_title}
      </DataPosition>

      <DataPosition
        style={{ left: "37.5rem", width: "5.5rem", top: "15.6rem" }}
      >
        {object?.employee?.salaryPointer?.["Monthly Rate"]}
      </DataPosition>

      {/* Leave Type Checkmarks */}
      {renderLeaveTypeCheckmarks()}

      {/* Location/Treatment Details */}
      {renderLocationDetails()}

      {/* Duration and Date Range */}
      <DataPosition style={{ left: "6.5rem", width: "14rem", top: "39.4rem" }}>
        {displayDays(calculations.durationDays)}
      </DataPosition>

      <DataPosition style={{ left: "6.5rem", width: "14rem", top: "41.6rem" }}>
        {`${formatDate(object?.startdate)} - ${formatDate(object?.enddate)}`}
      </DataPosition>

      {/* Leave Balances - Vacation */}
      {vacationLeave?.length > 0 && (
        <>
          <DataPosition
            style={{ left: "13rem", width: "5.9rem", top: "48.4rem" }}
          >
            {calculations.totalEarnedVacation}
          </DataPosition>
          <DataPosition
            style={{ left: "13rem", width: "5.9rem", top: "49.1rem" }}
          >
            {calculations.vacationDeduct}
          </DataPosition>
          <DataPosition
            style={{ left: "13rem", width: "5.9rem", top: "50rem" }}
          >
            {calculations.currentBalanceVacation}
          </DataPosition>
        </>
      )}

      {/* Leave Balances - Sick */}
      {sickLeave?.length > 0 && (
        <>
          <DataPosition
            style={{ left: "18.9rem", width: "5.9rem", top: "48.4rem" }}
          >
            {calculations.totalEarnedSickLeave}
          </DataPosition>
          <DataPosition
            style={{ left: "18.9rem", width: "5.9rem", top: "49.1rem" }}
          >
            {calculations.sickDeduct}
          </DataPosition>
          <DataPosition
            style={{ left: "18.9rem", width: "5.9rem", top: "50rem" }}
          >
            {Math.floor(calculations.currentBalanceSickLeave)}
          </DataPosition>
        </>
      )}

      {/* Approval Status */}
      <DataPosition
        style={{
          left: "27.2rem",
          width: "1rem",
          top: object?.forApproval ? "46.3rem" : "47.5rem",
        }}
      >
        ✔
      </DataPosition>

      {/* 7.C APPROVED FOR: days with pay / without pay / others */}
      {object?.dateRange?.length > 0 && (
        <>
          <DataPosition
            style={{
              left: "6.3rem",
              width: "auto",
              minWidth: "3.6rem",
              textAlign: "right",
              maxWidth: "5rem",
              fontSize: "10px",
              top: "55.3rem",
            }}
          >
            {calculations.daysWithPayDisplay}
          </DataPosition>

          <DataPosition
            style={{
              left: "6.3rem",
              width: "auto",
              minWidth: "3.6rem",
              textAlign: "right",
              maxWidth: "5rem",
              fontSize: "10px",
              top: "56.1rem",
            }}
          >
            {calculations.withoutPay > 0
              ? `${calculations.withoutPay} ${calculations.typeOfLeaveWithPay}`
              : ""}
          </DataPosition>
        </>
      )}

      {/* others (Specify) — e.g. "2.0 SL to VL" when SL/WL overflows to VL */}
      {calculations.othersSpecify && (
        <DataPosition
          style={{
            left: "6.9rem",
            width: "auto",
            minWidth: "3.6rem",
            textAlign: "right",
            maxWidth: "5rem",
            fontSize: "10px",
            top: "57rem",
          }}
        >
          {calculations.othersSpecify}
        </DataPosition>
      )}

      {/* HRDD Remarks */}
      <DataPosition
        style={{
          left: "28.1rem",
          width: "14.9rem",
          whiteSpace: "normal",
          top: "55.3rem",
          fontSize: "10px",
          lineHeight: 1.3,
          maxHeight: `${10 * 1.3 * 3}px`,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
        }}
      >
        {object?.hrddRemarks}
      </DataPosition>

      {/* Signatures */}
      <DataPosition
        style={{ left: "5.6rem", width: "20.4rem", top: "52.8rem" }}
      >
        MA. TERESA B. BASILIO
      </DataPosition>

      <DataPosition
        style={{ left: "5.6rem", width: "20.4rem", top: "53.3rem" }}
      >
        Acting Chief, Human Resource Management Section (HRMS)
      </DataPosition>

      <DataPosition
        style={{ left: "14.5rem", width: "20.4rem", top: "61.2rem" }}
      >
        ATTY. CRIS M. VILLALOBOS
      </DataPosition>

      <DataPosition
        style={{ left: "14.5rem", width: "20.4rem", top: "61.7rem" }}
      >
        Chief, Human Resource Development Division (HRDD)
      </DataPosition>
    </div>
  );
}
